"use client";

import { Bot, Bug, ChevronsUpDown, LogIn, LogOut, Mail } from "lucide-react";
import { signOut } from "next-auth/react";
import { useTranslations } from "next-intl";

import { useOnlineStatus } from "@/app/hooks/useOnlineStatus";
import { useSession } from "@/app/hooks/useSession";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FadeImage } from "@/components/ui/fade-image";
import { Link } from "@/i18n/navigation";
import { clearStorage } from "@/lib/storage";
import { cn } from "@/lib/utils";

interface NavFooterProps {
  variant: "drawer" | "rail";
  onItemClick?: () => void; // Drawer-only — close-on-tap.
}

// Bottom block on the side nav surfaces (mobile AppDrawer + desktop
// DesktopRail). Compact account row that mirrors the Linear / Notion /
// Vercel sidebar pattern: avatar + name + chevron, with secondary
// actions (Contact, Report bug, Sign out) tucked behind a popover so
// the rail stays uncluttered. Unauthenticated state collapses to a
// single Sign in row.
//
// No top border; the row floats over the nav with just padding around
// it. Settings is a top-level nav destination.
export function NavFooter({ variant, onItemClick }: NavFooterProps) {
  const t = useTranslations("common");
  const tNav = useTranslations("nav");
  const { data: session, isAuthenticated } = useSession();
  const { isOffline } = useOnlineStatus();

  // The way into /agents sits above the account row, signed in or not, so every page links to it.
  const agentsLink = (
    <Link
      href="/agents"
      onClick={() => onItemClick?.()}
      className={cn(
        "flex w-full items-center gap-3 rounded-md px-2 py-2",
        "text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground",
        variant === "drawer" && "py-2.5 text-base"
      )}
    >
      <Bot className="size-4" aria-hidden />
      <span>{tNav("agents")}</span>
    </Link>
  );

  if (!isAuthenticated) {
    return (
      <div className="px-2 pb-3">
        {agentsLink}
        <Link
          href="/auth/signin"
          onClick={() => onItemClick?.()}
          className={cn(
            "flex w-full items-center gap-3 rounded-md px-2 py-2",
            "text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground",
            variant === "drawer" && "py-2.5 text-base"
          )}
        >
          <LogIn className="size-4" aria-hidden />
          <span>{t("signIn")}</span>
        </Link>
      </div>
    );
  }

  const name = session?.user?.name ?? session?.user?.email ?? "Account";
  const email = session?.user?.email ?? null;
  const image = session?.user?.image ?? null;

  return (
    <div className="px-2 pb-3">
      {agentsLink}
      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn(
            "group flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 outline-none",
            "transition-colors hover:bg-muted/60 focus-visible:bg-muted/60",
            "data-[state=open]:bg-muted/60"
          )}
        >
          <Avatar src={image} name={name} />
          <div className="min-w-0 flex-1 text-start">
            <p className="truncate text-sm font-medium leading-tight">{name}</p>
            {email && name !== email && (
              <p className="truncate text-xs leading-tight text-muted-foreground">{email}</p>
            )}
          </div>
          <ChevronsUpDown
            className="size-3.5 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
            aria-hidden
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          side="top"
          sideOffset={6}
          className="w-56"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DropdownMenuItem
            className="text-base"
            onSelect={mailtoSelect("contact", session, onItemClick)}
          >
            <Mail className="size-5" aria-hidden />
            <span>{t("contactUs")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-base"
            onSelect={mailtoSelect("bug", session, onItemClick)}
          >
            <Bug className="size-5" aria-hidden />
            <span>{t("reportBug")}</span>
          </DropdownMenuItem>
          {!isOffline && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-base"
                onSelect={() => {
                  onItemClick?.();
                  clearStorage();
                  signOut({ callbackUrl: "/" });
                }}
              >
                <LogOut className="size-5" aria-hidden />
                <span>{t("signOut")}</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function Avatar({ src, name }: { src: string | null; name: string }) {
  if (src) {
    return (
      <FadeImage
        src={src}
        alt=""
        width={28}
        height={28}
        className="size-7 shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

type Session = ReturnType<typeof useSession>["data"];

function mailtoSelect(
  kind: "bug" | "contact",
  session: Session,
  onItemClick: (() => void) | undefined
): () => void {
  return () => {
    onItemClick?.();
    if (typeof window === "undefined") return;
    const subject = kind === "bug" ? "Bug Report - Nombox" : "Inquiry or Feedback - Nombox";
    const lead =
      kind === "bug"
        ? "Bug description, steps to reproduce, expected, actual:"
        : "[Please describe your inquiry or feedback]";
    const sys = {
      ts: new Date().toISOString(),
      ua: navigator.userAgent,
      platform: navigator.platform,
      lang: navigator.language,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      email: session?.user?.email ?? "Not logged in",
    };
    const body = `Hi Nombox Team,\n\n${lead}\n\n---\nSystem info:\n- Timestamp: ${sys.ts}\n- User: ${sys.email}\n- Browser: ${sys.ua}\n- Platform: ${sys.platform}\n- Viewport: ${sys.viewport}\n- Language: ${sys.lang}\n`;
    window.location.href = `mailto:nomboxapp@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
}
